import { withHTMLComponent } from '@updater-local/custom-element-v1';
import { withFormValidation } from '@updater-components/form-element';
import { STATES, REQUIRED_FIELDS } from './constants';
import '@updater-components/input';
import '@updater-components/input/presets/zip';
import '@updater-components/select';
import styles from './index.css';
import html from './index.html';

const REFLECTED_ATTRIBUTES = [
  { name: 'required', boolean: true },
  { name: 'static', boolean: true },
  { name: 'name' },
];

export default class Address extends withFormValidation(
  withHTMLComponent(styles, html)()
) {
  static props = ['value'];

  static get observedAttributes() {
    return REFLECTED_ATTRIBUTES.map(attr => attr.name);
  }

  static get reflectedAttributes() {
    return REFLECTED_ATTRIBUTES;
  }

  attributeChangedCallback(name, oldValue, newValue) {
    super.attributeChangedCallback(name, oldValue, newValue);

    switch (name) {
      case 'static':
        this._static.innerHTML = '';
        this.static &&
          this._static.appendChild(
            document
              .createRange()
              .createContextualFragment(getStaticHTML(this.value))
          );
        break;
      case 'required':
        REQUIRED_FIELDS.forEach(field => {
          const ctrl = this._controls[field];
          this.required
            ? ctrl.setAttribute('required', '')
            : ctrl.removeAttribute('required');
        });
        break;
    }
  }

  constructor() {
    super();
    this._input = this.shadowRoot.querySelector('#input');
    this._static = this.shadowRoot.querySelector('#static-address');
    this._controls = {
      street: this.shadowRoot.querySelector('[name="street"]'),
      unit: this.shadowRoot.querySelector('[name="unit"]'),
      city: this.shadowRoot.querySelector('[name="city"]'),
      state: this.shadowRoot.querySelector('[name="state"]'),
      postalCode: this.shadowRoot.querySelector('[name="postalCode"]'),
    };
  }

  connected() {
    this._controls.state.options = STATES;
    this.static = isAddressComplete(this.value);

    this.addEventListener('click', this._handleClick);
    this.addEventListener('updvalidation', this._handleValidation);
    this._iterateControls(ctrl => {
      ctrl.addEventListener('change', this._handleInputOrChange);
      ctrl.addEventListener('input', this._handleInputOrChange);
      ctrl.addEventListener('updvalidation', this._handleControlValidation);
    });
  }

  disconnected() {
    this.removeEventListener('click', this._handleClick);
    this.removeEventListener('updvalidation', this._handleValidation);
    this._iterateControls(ctrl => {
      ctrl.removeEventListener('change', this._handleInputOrChange);
      ctrl.removeEventListener('input', this._handleInputOrChange);
      ctrl.removeEventListener('updvalidation', this._handleControlValidation);
    });
  }

  updated({ value }) {
    this._iterateControls((ctrl, key) => {
      ctrl.value = value[key] || '';
    });

    this._input.required = this.required;
    this._input.value = isAddressComplete(this.value)
      ? stringifyAddress(this.value)
      : null;
  }

  _handleClick = () => {
    if (this.static) {
      this.static = false;
    }
  };

  _handleInputOrChange = event => {
    const { target, type } = event;
    // We want `address` to act like a single form control even though its
    // composed of many form controls. Collect the individual controls' events
    // and redispatch them as if they came from this control.
    event.stopPropagation();

    this.value = {
      ...this.value,
      [target.name]: target.value,
    };

    this.dispatchEvent(
      new CustomEvent(type, {
        bubbles: true,
        composed: true,
        detail: this.value,
      })
    );

    if (this._hasBeenValidatedAlready) {
      this.checkValidity();
    }
  };

  _handleControlValidation = event => {
    // Stop propagation since we want this component to
    // control when the `updvalidation` event is emitted.
    event.stopPropagation();
  };

  _handleValidation = ({ target }) => {
    const isValid = target.validity.valid;

    // Apply for styling based on whether the component
    // as a whole is invalid.
    this._iterateControls(ctrl => {
      ctrl.setAttribute('aria-invalid', !isValid);
      ctrl.classList.add('upd-validated');
    });
  };

  _iterateControls(fn) {
    Object.keys(this._controls).forEach(key => {
      fn(this._controls[key], key);
    });
  }
}

customElements.define('upd-moveiq-address', Address);

export function isAddressComplete(address) {
  return REQUIRED_FIELDS.every(field => !!(address || {})[field]);
}

function stringifyAddress({ street, unit, city, state, postalCode }) {
  return [street, unit, city, state, postalCode].filter(Boolean).join(', ');
}

function getStaticHTML(value) {
  // Convert all falsy value to undefined
  const cleanedValue = Object.keys(value).reduce((newValue, key) => {
    newValue[key] = value[key] || undefined;
    return newValue;
  }, {});

  const {
    street = '',
    unit = '',
    city = '',
    state = '',
    postalCode = '',
  } = cleanedValue;

  return `
    <div>${street} ${unit}</div>
    <div>${city}, ${state} ${postalCode}</div>
  `;
}
